import { useState } from "react";

export interface FC2Product {
  id: string;
  title: string;
  link: string;
  thumbnail: string;
  price: number;
  priceLabel: string;
  rewardRate: number;
  rewardPt: number;
  pubDate: string;
  description: string;
  seller?: string;
}

interface FC2ProductCardProps {
  product: FC2Product;
  affiliateId?: string;
}

export default function FC2ProductCard({ product, affiliateId }: FC2ProductCardProps) {
  const [imgError, setImgError] = useState(false);

  const buildLink = (href: string) => {
    if (!href) return "#";
    // アフィリエイトIDがすでにリンクに含まれている場合はそのまま返す
    if (href.includes("affid=")) return href;
    if (!affiliateId) return href;
    try {
      const u = new URL(href);
      u.searchParams.set("affid", affiliateId);
      return u.toString();
    } catch {
      const sep = href.includes("?") ? "&" : "?";
      return `${href}${sep}affid=${affiliateId}`;
    }
  };

  const finalLink = buildLink(product.link);

  return (
    <a
      href={finalLink}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-rose-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
    >
      {/* サムネイル */}
      <div className="w-full aspect-[3/4] bg-gray-100 overflow-hidden flex-shrink-0 relative">
        {!imgError && product.thumbnail ? (
          <img
            src={product.thumbnail}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-rose-50 to-pink-50">
            <i className="ri-image-line text-3xl text-rose-200 mb-1" />
            <span className="text-xs text-rose-200">FC2</span>
          </div>
        )}
        {/* 報酬率バッジ */}
        {product.rewardRate > 0 && (
          <div className="absolute top-2 left-2 bg-rose-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap">
            {product.rewardRate}%
          </div>
        )}
      </div>

      {/* 情報 */}
      <div className="p-2.5 flex flex-col gap-1.5 flex-1">
        <p className="text-xs text-gray-800 font-medium leading-snug line-clamp-2 group-hover:text-rose-700 transition-colors min-h-[2.5rem]">
          {product.title || "FC2コンテンツ"}
        </p>

        <div className="flex flex-col gap-1 mt-auto">
          {product.priceLabel && (
            <span className="text-xs font-bold text-gray-700">
              {product.priceLabel}
            </span>
          )}
          {product.rewardPt > 0 && (
            <span className="text-xs text-emerald-600 font-medium">
              報酬 +{product.rewardPt.toLocaleString()}pt
            </span>
          )}
          {product.seller && (
            <span className="text-xs text-gray-400 truncate">{product.seller}</span>
          )}
        </div>
      </div>
    </a>
  );
}
